import {withLocalization} from '@epic-core/localization';
import AnimateObject from 'epic-fortnite-react/components/common/AnimateObject';
import epicTracking from 'epic-tracking';
import {motion, useMotionValue, useSpring, useTransform} from 'framer-motion';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import styled from 'styled-components';
import root from 'window-or-global';

import AnimateString from '../common/AnimateString';
import CTAButton from '../common/CTAButton';
import VideoOverlay from '../common/VideoOverlay';
import VideoPlayButton from '../common/VideoPlayButton';

import {Device} from '../../constants';

import {StyledPlayButtonWrapper} from './CtaFooter';

const StyledCtaContent = styled.div`
  display: flex;
  padding: 0;
  text-align: center;
  margin-bottom: 0;

  @media screen and ${Device.laptopL} {
    width: 100%;
  }
`;

const StyledSeasonHeadingWrapper = styled.div`
  color: #fff;
  font-size: 0.7em;
  &&&& h3 {
    letter-spacing: 5px;
  }
`;

export const StyledBattlePassFooterContainer = styled.section`
  position: relative;
  clip-path: polygon(0% 0%, 0% 100%, 100% 100%, 100% 4vw);
  margin-top: -4vw;
  z-index: 2;

  @media screen and ${Device.mobileL} {
    height: unset;
    max-height: 100vh;

    p {
      width: 100%;
    }
  }
`;

const StyledCtaDescription = styled.div`
  width: 100%;
  margin: 0 auto;
  color: #fff;
  font-size: 1.4em;
  margin-bottom: 1em;
  padding: 1em;
  margin: 1em 0;

  &&&& {
    p {
      margin: 0;
      line-height: 1.7;
      font-style: italic;
    }
  }

  @media screen and ${Device.laptopL} {
    width: 100%;
    margin-bottom: 0;
    padding: 0;
  }

  @media screen and ${Device.tablet} {
    width: 100%;
  }

  @media screen and ${Device.mobileL} {
    width: 100%;
    font-size: 1em;
    margin: 0;
    margin-top: 1em;
  }
`;

const StyledCtaCaptionContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media screen and ${Device.tablet} {
    text-align: center;
  }

  @media screen and ${Device.mobileL} {
    padding: 0.75em;
    font-size: 0.75em;
    text-align: center;
  }

  p {
    color: #99cdfb;
    text-transform: none !important;
    font-size: 0.8em;
    font-family: OpenSans, sans-serif !important;
  }
  img {
    width: 16px;
    height: 16px;
    margin-right: 0.5em;
  }
`;

const StyledCtaHeaderContent = styled(StyledCtaContent)`
  && h3 {
    font-size: 2em;
    letter-spacing: 10px;
  }

  @media screen and ${Device.laptopL} {
    width: 100%;
    margin: 0 auto;
  }

  @media screen and ${Device.mobileL} {
    &&&& h3 {
      font-size: 1.5em;
    }
  }
`;

const StyledFortniteLogo = styled.img`
  height: 80px;

  @media screen and ${Device.mobileL} {
    height: 50px;
  }
`;

const StyledCtaTitleImage = styled.img`
  width: 100%;

  @media screen and ${Device.laptopL} {
    width: 60%;
  }

  @media screen and ${Device.mobileL} {
    width: 100%;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;

  @media screen and ${Device.laptopL} {
    width: 75%;
  }

  @media screen and ${Device.tablet} {
    width: 100%;
    padding: 1em;
    margin-bottom: 2em;
  }
`;

const StyledBackgroundImageFull = styled.img`
  width: 100%;
  height: auto;
`;

const ImageCta = ({data, mobile, tablet, getMessage}) => {
  const [visible, setVisibility] = useState(false);
  const [isMobile, setMobile] = useState(false);
  const {
    badge,
    title,
    titleAsImage,
    fnLogo,
    subtitle,
    cta,
    desktopImg,
    mobileImg,
    videoId,
    caption,
    captionImage,
    captionLine2,
    imgAlt,
    description,
  } = data;
  const bgImg = isMobile && mobileImg ? mobileImg : desktopImg;
  const [isPlay, setPlay] = useState(false);

  const xPos = useMotionValue(root ? root.innerWidth / 2 : 600);
  const x = useTransform(xPos, [0, root ? root.innerWidth : 1200], [20, -20], {
    clamp: true,
  });
  const xSpring = useSpring(x, {
    damping: 120,
  });
  const handleMouse = (e) => {
    const {clientX, pageX} = e;
    if (!pageX && clientX) {
      xPos.set(clientX);
    } else if (pageX) {
      xPos.set(pageX);
    }
  };

  const isMobileCheck = React.useCallback(() => {
    const ww = root.innerWidth;
    if (mobile) {
      setMobile(true);
    } else if (ww <= 991 && !isMobile) {
      setMobile(true);
    } else if (ww > 991 && isMobile) {
      setMobile(false);
    }
  }, [mobile, setMobile, root, isMobile]);

  const toggleVisibility = React.useCallback(
    (visibility) => {
      setVisibility(visibility);
      if (visibility) {
        epicTracking.trackEvent('Interaction', {
          interactionType: 'scrollViewSection',
          eventCategory: 'BattlePass CTA Section',
          eventAction: 'CtaSection-inView',
        });
      }
    },
    [setVisibility]
  );

  React.useEffect(() => {
    isMobileCheck();
    root.addEventListener('resize', isMobileCheck, true);
    root.addEventListener('mousemove', handleMouse, true);

    return () => {
      root.removeEventListener('resize', isMobileCheck, true);
      root.removeEventListener('mousemove', handleMouse, true);
    };
  }, [isMobileCheck, handleMouse, root]);

  return (
    <VisibilitySensor
      onChange={toggleVisibility}
      active={!visible}
      partialVisibility
      minTopValue={0}>
      <StyledBattlePassFooterContainer>
        {captionImage ? <img src={captionImage} alt={imgAlt} /> : null}
        {bgImg && <StyledBackgroundImageFull src={bgImg} alt={imgAlt} />}
        <div>
          <StyledContentWrapper>
            <StyledCtaHeaderContent>
              <motion.div style={{x: visible && !mobile && !tablet ? xSpring : 0, width: '100%'}}>
                {fnLogo && (
                  <AnimateObject
                    minTop={20}
                    speed={1.5}
                    from={{y: `+=150`, autoAlpha: 0}}
                    to={{y: 0, autoAlpha: 1, delay: 0.4}}>
                    <StyledFortniteLogo src={fnLogo} alt="" />
                  </AnimateObject>
                )}
                {subtitle && (
                  <StyledSeasonHeadingWrapper>
                    <AnimateString
                      text={subtitle}
                      type="h3"
                      speed={1.5}
                      delay={0}
                      autoAlpha={0}
                      yPos={100}
                      minTop={20}
                      scaleFrom={1}
                      staggerDelay={0}
                    />
                  </StyledSeasonHeadingWrapper>
                )}
                {titleAsImage && (
                  <AnimateObject
                    minTop={20}
                    speed={1.5}
                    from={{y: `+=150`, autoAlpha: 0}}
                    to={{y: 0, autoAlpha: 1, delay: 0.4, width: '75%'}}>
                    <StyledCtaTitleImage src={titleAsImage} alt="" />
                  </AnimateObject>
                )}
                {!titleAsImage && title && (
                  <AnimateString
                    text={title}
                    type="h2"
                    speed={1.5}
                    delay={0.3}
                    autoAlpha={0}
                    yPos={100}
                    minTop={20}
                    scaleFrom={1}
                    staggerDelay={0}
                  />
                )}
              </motion.div>
            </StyledCtaHeaderContent>

            <StyledCtaContent>
              <motion.div style={{x: visible && !mobile && !tablet ? xSpring : 0}}>
                {description && (
                  <StyledCtaDescription>
                    <AnimateString
                      key="imageCta-description"
                      text={description}
                      type="p"
                      speed={1.5}
                      delay={0.5}
                      autoAlpha={0}
                      yPos={100}
                      minTop={20}
                      scaleFrom={1}
                      staggerDelay={0}
                    />
                  </StyledCtaDescription>
                )}
                {badge && <img src={badge} className="image-cta-badge" alt="badge" />}
                {cta && <CTAButton ctaConfig={cta} theme="brightYellow" />}
              </motion.div>
            </StyledCtaContent>
          </StyledContentWrapper>
          {videoId ? (
            <StyledPlayButtonWrapper>
              <AnimateObject
                minTop={20}
                speed={1.5}
                from={{y: `+=150`, autoAlpha: 0}}
                to={{y: 0, autoAlpha: 1, delay: 0.4}}>
                <>
                  <p>{getMessage('epic.fortnite.trailer')}</p>
                  <VideoPlayButton onClick={() => setPlay(true)} className="bp-videoplay" />
                </>
              </AnimateObject>
            </StyledPlayButtonWrapper>
          ) : null}
          {caption && (
            <StyledCtaCaptionContainer>
              {captionImage ? <img src={captionImage} alt="" /> : null}
              <p>{caption}</p>
              <p>{captionLine2}</p>
            </StyledCtaCaptionContainer>
          )}
        </div>
        {videoId && isPlay ? (
          <div className="videoOverlay-wrapper">
            <VideoOverlay
              key={`bp-img-cta-${videoId}`}
              videoID={videoId}
              isLauncher={false}
              play={isPlay}
              close={() => setPlay(false)}
              closeButton
              show={isPlay}
            />
          </div>
        ) : null}
      </StyledBattlePassFooterContainer>
    </VisibilitySensor>
  );
};

ImageCta.propTypes = {
  data: PropTypes.object,
  mobile: PropTypes.bool,
  getMessage: PropTypes.func,
  tablet: PropTypes.bool,
};

export default withLocalization(ImageCta);
